// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT

import { MetricBottomSummary } from '@lfx/key-projects/key-projects.model';
import { BaseMetricEntity, BaseMetricConfig, ChartType, ScoreLevel } from './base-metric.entity';

/**
 * Configuration interface for EventSponsorships entity
 */
type EventSponsorshipsConfig = BaseMetricConfig;

/**
 * EventSponsorships Entity - Encapsulates all event sponsorships-specific business logic
 *
 * This class consolidates configuration, scoring rules, and display logic
 * for the event sponsorships ecosystem metric.
 */
export class EventSponsorships extends BaseMetricEntity {
  static readonly METRIC_TYPE = 'event_sponsorships' as const;

  /**
   * Centralized configuration object containing all static values
   */
  protected static readonly CONFIG: EventSponsorshipsConfig = {
    metricType: 'event_sponsorships',
    display: {
      displayName: 'Event Sponsorships',
      chartType: 'area' as ChartType,
      tooltip: {
        definition: 'What percentage of event attendees are reached by our company through event sponsorship? Metric considers the sponsored event attendee count weighted by sponsorship level.',
        scoringRules: [
          '0 event attendees reached: = 0 points',
          '0.1 to 25% of event attendees reached = 0.33 points',
          '25.1 to 50% of event attendees reached = 0.66 points',
          '>50% of event attendees reached = 1 point',
          '0 points = <b>Silent</b>',
          '0.33 points = <b>Participating</b>',
          '0.66 points = <b>Contributing</b>',
          '1 point = <b>Leading</b>'
        ]
      }
    },
    bottomSummary: {
      zeroValue: {
        description: `Our company has no event sponsorships for {context}.`,
      },
      positiveValue: {
        descriptionTemplate: `Our company reached {percentage}% of event attendees through event sponsorship.`,
      },
      nullValue: {
        description: this.NULL_VALUE_DESCRIPTION,
      }
    }
  };

  /**
   * Get display configuration from config object
  */
  public static getDisplayConfig(): BaseMetricConfig['display'] {
      return this.CONFIG.display;
  }

  /**
   * Get full configuration object
   */
  public static getConfig(): BaseMetricConfig {
      return this.CONFIG;
  }

  static getBottomSummary(percentage: string, allTimeTotal: number, isLfProject: boolean, level: ScoreLevel, projectType?: string | null): MetricBottomSummary {
    if (isLfProject === false) {
      return this.getNonLfProjectBottomSummary(this.CONFIG);
    }

    if (allTimeTotal === 0) {
      return this.getNullValueBottomSummary(this.CONFIG);
    }

    const percentageValue = percentage || '0.0';
    const numericValue = parseFloat(percentageValue);
    const styles = this.getStylesByLevel(level);
    const contextText = projectType?.toLowerCase() === 'foundation'
      ? 'this foundation'
      : 'this project';

    if (numericValue === 0) {
      return {
        title: this.CONFIG.display.displayName,
        description: this.formatDescription(this.CONFIG.bottomSummary.zeroValue.description, {context: contextText}),
        icon: styles.icon,
        iconColor: styles.iconColor,
        borderColor: styles.borderColor
      };
    }

    const description = this.formatDescription(
      this.CONFIG.bottomSummary.positiveValue.descriptionTemplate,
      {
        percentage: percentageValue
      }
    );

    return {
      title: this.CONFIG.display.displayName,
      description,
      icon: styles.icon,
      iconColor: styles.iconColor,
      borderColor: styles.borderColor
    };
  }


}
